import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { RUM_PURPOSE, useRumAction, useRumError } from 'services/observability/datadog';
import { CreateVariantsFromSuggestionsProps, ERROR_CODE } from '../types';
import { variantAlphabet } from '../utils';
import { useExperimentsTranslations } from './useTranslations';

export const useSuggestionsAsVariants = () => {
  const { t } = useExperimentsTranslations();
  const addSuccessSignal = useRumAction(RUM_PURPOSE.CopyAssist);
  const addErrorSignal = useRumError();
  const dispatch = useDispatch();
  const [isVariantsCreating, setIsVariantsCreating] = useState(false);
  const COPY_ASSIST_ADDED_AS_VARIANTS = 'COPY_ASSIST_ADDED_AS_EXPERIMENT_VARIANTS';

  const dispatchSuccessToaster = () =>
    dispatch(addPositiveToast(t('successMessage.addSuggestionsAsVariants')));

  const dispatchFullFailureErrorToaster = (e: unknown) =>
    dispatch(
      addNegativeToastWithErrorId(
        t('errorMessage.addSuggestionsAsVariantsFullFailure'),
        'useSuggestionsAsVariants',
        e,
      ),
    );

  const dispatchPartialFailureErrorToaster = (e: unknown) =>
    dispatch(
      addNegativeToastWithErrorId(
        t('errorMessage.addSuggestionsAsVariantsPartialFailure'),
        'useSuggestionsAsVariants',
        e,
      ),
    );

  const logError = (e: unknown) => {
    const errorMessage = e instanceof Error ? e.message : `${e}`;
    addErrorSignal(new Error(errorMessage), COPY_ASSIST_ADDED_AS_VARIANTS);
  };

  const logSuccess = (context: Partial<CreateVariantsFromSuggestionsProps>) =>
    addSuccessSignal(COPY_ASSIST_ADDED_AS_VARIANTS, context);

  const createVariantsFromSuggestions = async ({
    experimentId,
    currentVariantId,
    currentVariantCount,
    suggestions,
    fieldName,
    currentLocale,
    updateExperimentVariant,
    createExperimentVariant,
    closeCopyAssistDropdown,
    deleteExperimentVariant,
    refetch,
    validateTemplate,
  }: CreateVariantsFromSuggestionsProps) => {
    setIsVariantsCreating(true);

    const newlyCreatedVariantIds: number[] = [];
    const erroredVariantUpdateIds: number[] = [];
    const variantIdsToBeEnrichedWithCopyAssist: number[] = [];
    const nosOfNewVariantsToCreate = suggestions.length - 1;

    const deleteCreatedVariants = async (createdVariantIds: number[]) => {
      try {
        // Deleting the variants that we created
        await Promise.allSettled(
          createdVariantIds.map((variantId) =>
            deleteExperimentVariant({ variantId }, currentLocale, false),
          ),
        );
      } catch (error) {
        // Ok, we tried our best to clean up
        logError(error);
      }
    };

    try {
      // First we want to create the new variants for
      // our suggestions in a sequential order.
      for (let index = 0; index < nosOfNewVariantsToCreate; index++) {
        const variantName = variantAlphabet(
          currentVariantCount + index,
          t('variantCard.prefix.variant'),
        );

        try {
          /**
           * The reason why we want to execute sequentially
           * is to ensure that the variant names are in order
           * */
          // eslint-disable-next-line no-await-in-loop
          const variantCreationResult = await createExperimentVariant(
            currentVariantId,
            currentLocale,
            variantName,
            false,
          );

          if (variantCreationResult.isSuccess) {
            newlyCreatedVariantIds.push(variantCreationResult.mutatedVariant.id);
          } else {
            /**
             * Even if we fail to create any one of the new variant which is required
             * We safely want to exit from creating the other variants.
             *
             * We will be deleting the variants which did get created successfully. We do this
             * so that we don't end up with offset variant alphabets and variant names
             * eg. [c] Variant D
             */
            throw new Error(ERROR_CODE.SOME_VARIANT_CREATION_FAILED);
          }
        } catch (variantCreationError) {
          // In case of any unexpected errors while creating a new variant
          logError(variantCreationError);
          throw new Error(ERROR_CODE.SOME_VARIANT_CREATION_FAILED);
        }
      }

      // We want to enrich the current variant along with the newly created variants with the CA suggestions
      variantIdsToBeEnrichedWithCopyAssist.push(currentVariantId, ...newlyCreatedVariantIds);

      if (variantIdsToBeEnrichedWithCopyAssist.length === 1) {
        /**
         * This is just a safety check if in case the suggestion object was empty
         * for some reason. We want to exit out without doing any damage
         */
        throw new Error(ERROR_CODE.ALL_VARIANT_CREATION_FAILED);
      }

      /**
       * Make parallel calls to update all the variants which
       * needs to be enriched with copy assist
       */
      await Promise.allSettled(
        variantIdsToBeEnrichedWithCopyAssist.map((variantId, index) =>
          // Disable error toasters and redux updates while updating variants
          updateExperimentVariant(variantId, { [fieldName]: suggestions[index] }, false, false)
            .then((result) => {
              // Record any variant creation failure
              if (!result.isSuccess) {
                erroredVariantUpdateIds.push(variantId);
              }
            })
            .catch((variantUpdateError) => {
              logError(variantUpdateError);
              erroredVariantUpdateIds.push(variantId);
            }),
        ),
      );

      /**
       * We need to run template validation on all the
       * variants that were updated by copy assist
       */
      await Promise.allSettled(
        variantIdsToBeEnrichedWithCopyAssist.map((variantId) => validateTemplate(variantId)),
      );

      if (erroredVariantUpdateIds.length === variantIdsToBeEnrichedWithCopyAssist.length) {
        throw new Error(ERROR_CODE.ALL_VARIANT_UPDATES_FAILED);
      } else if (erroredVariantUpdateIds.length > 0) {
        throw new Error(ERROR_CODE.SOME_VARIANT_UPDATES_FAILED);
      }

      /**
       * If, everything is successful so far. Then we want to close the CA dropdown
       * And, display a success toaster message and reFetch the variant list
       */
      await refetch();
      closeCopyAssistDropdown();
      logSuccess({
        experimentId,
        currentVariantId,
        fieldName,
      });
      dispatchSuccessToaster();
    } catch (e) {
      /**
       * MAIN ERROR HANDLING SECTION FOR ADDING
       * COPY ASSIST SUGGESTIONS AS VARIANTS
       */

      if (e instanceof Error) {
        switch (e.message) {
          case ERROR_CODE.ALL_VARIANT_CREATION_FAILED:
            dispatchFullFailureErrorToaster(e);
            break;

          /**
           * Delete just the variants that we didn't update
           * And, refetch the variant list and close the
           * copy assist dropdown
           */
          case ERROR_CODE.SOME_VARIANT_UPDATES_FAILED: {
            const newlyCreatedVariantsWhichDidNotUpdate = erroredVariantUpdateIds.filter((x) =>
              newlyCreatedVariantIds.includes(x),
            );
            await deleteCreatedVariants(newlyCreatedVariantsWhichDidNotUpdate);
            await refetch();
            closeCopyAssistDropdown();
            dispatchPartialFailureErrorToaster(e);
            break;
          }

          /**
           * Delete any newly created variants when we only succeeded in partial
           * variant creation or if none of the variants updated or
           * if we aren't sure what caused the error.
           * We want to have the copy assist dropdown open in this case
           * so that the user can try again
           */
          case ERROR_CODE.SOME_VARIANT_CREATION_FAILED:
          case ERROR_CODE.ALL_VARIANT_UPDATES_FAILED:
          default:
            await deleteCreatedVariants(newlyCreatedVariantIds);
            dispatchFullFailureErrorToaster(e);
            break;
        }
      } else {
        /**
         * Unexpected error happened. So, the safe bet would be to delete any
         * newly created variants.
         */
        logError(e);
        await deleteCreatedVariants(newlyCreatedVariantIds);
        dispatchFullFailureErrorToaster(e);
      }
    }

    // We are all done!!
    setIsVariantsCreating(false);
  };

  return { isVariantsCreating, createVariantsFromSuggestions };
};
